/**
 * DeviceAssignDrawer — Searchable device picker for assigning a device to the area.
 *
 * Mirrors the AreaDeviceDrawer "Available" tab pattern from AreasPage:
 *   - Only shows devices where is_area_device is true
 *   - Excludes devices already assigned to THIS area
 *   - Sorts: unassigned first, then assigned-to-other-areas
 *   - Shows "Assigned to {area_name}" indicator for devices on other areas
 *   - Requires confirmation before reassigning from another area
 */

import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';

/**
 * Render device name as a link only when a detail page exists for it.
 *
 * Linkable:
 *   - manufacture_id === 4 (PalCare devices)
 *   - manufacture_id === 1 (System devices) EXCEPT device_type_id 10, 11,
 *     56, 57 — those are virtual/hardware-only types without a profile page
 *
 * Not linkable (rendered as plain text):
 *   - manufacture_id === 10 (SIP / speakers — no per-device profile)
 *   - everything else
 */
const NON_LINKABLE_SYSTEM_DEVICE_TYPES = new Set([10, 11, 56, 57]);
const DeviceNameLink = ({ device }) => {
  const mid = parseInt(device.manufacture_id, 10);
  const tid = parseInt(device.device_type_id, 10);
  const isLinkable =
    mid === 4
    || (mid === 1 && !NON_LINKABLE_SYSTEM_DEVICE_TYPES.has(tid));
  if (isLinkable) {
    return (
      <Link to={`/community/devices/${mid}/${device.device_id}`} className="ap-device-picker__link">
        {device.device_name || 'Unnamed Device'}
      </Link>
    );
  }
  return <>{device.device_name || 'Unnamed Device'}</>;
};

const DeviceAssignDrawer = ({
  open, onClose, onAssign, saving,
  availableDevices, areaDevices, areaName,
  // Matches AreasPage's drawer: SIP (manufacture_id 10) and System (mid 1
  // + device_category_id 4/5) devices are restricted — only role weight
  // > 80 may assign them. Non-admins simply don't see them in the
  // available list so they can't be picked.
  canManageRestrictedDevices = true,
  isRestrictedDevice,
  onAddNewDevice,
}) => {
  const checkRestricted = (d) =>
    typeof isRestrictedDevice === 'function' ? isRestrictedDevice(d) : false;
  const [search, setSearch] = useState('');
  const [confirmDevice, setConfirmDevice] = useState(null);
  // Optional `descriptions` override, captured in the confirm step.
  const [assignLocation, setAssignLocation] = useState('');

  // Reset state when drawer opens
  const prevOpen = useRef(false);
  useEffect(() => {
    if (open && !prevOpen.current) {
      setSearch('');
      setConfirmDevice(null);
      setAssignLocation('');
    }
    prevOpen.current = open;
  });

  /**
   * Filtered & sorted available devices:
   * 1. Only include area-capable devices (is_area_device)
   * 2. Exclude devices already assigned to THIS area
   * 3. Apply search filter
   * 4. Sort: unassigned first, then assigned-to-other-areas
   */
  const assignedIds = useMemo(() => {
    return new Set((areaDevices || []).map((d) => d.device_id));
  }, [areaDevices]);

  const filteredDevices = useMemo(() => {
    const term = search.toLowerCase().trim();

    return (availableDevices || [])
      .filter((d) => {
        // Only area-capable devices (is_area_device can be 1/0 or true/false)
        if (!Number(d.is_area_device)) return false;
        // Exclude devices already assigned to THIS area
        if (assignedIds.has(d.device_id)) return false;
        // Restricted devices (SIP / system cat 4-5) are hidden from non-admins
        // so they can't be assigned. Mirrors AreasPage's drawer behavior.
        if (!canManageRestrictedDevices && checkRestricted(d)) return false;
        // Search filter
        if (term) {
          return (
            (d.device_name || '').toLowerCase().includes(term) ||
            (d.manufacture_uuid || '').toLowerCase().includes(term) ||
            (d.descriptions || '').toLowerCase().includes(term) ||
            (d.model_name || '').toLowerCase().includes(term) ||
            (d.area_name || '').toLowerCase().includes(term)
          );
        }
        return true;
      })
      .sort((a, b) => {
        // Unassigned first, then assigned-to-other-areas
        const aAssigned = (a.area_id && Number(a.is_active)) ? 1 : 0;
        const bAssigned = (b.area_id && Number(b.is_active)) ? 1 : 0;
        if (aAssigned !== bAssigned) return aAssigned - bAssigned;
        return (a.device_name || '').localeCompare(b.device_name || '');
      });
  }, [availableDevices, assignedIds, search, canManageRestrictedDevices, isRestrictedDevice]);

  /** A device already live in another area is being moved, not just attached. */
  const isReassign = !!(confirmDevice?.area_id && Number(confirmDevice?.is_active));

  /** Every assign goes through the confirm step — it's where the optional
   *  "Location within area" is captured, matching the AreasPage drawer.
   *
   *  Seeded with the device's current description rather than left blank, so the
   *  field states the outcome instead of asking the user to infer it from a hint.
   *  Clearing it therefore clears the description — assignDeviceToArea
   *  distinguishes an omitted argument from an empty one (areaProfileApi.js). */
  const handleAssign = useCallback((device) => {
    setAssignLocation(device?.descriptions || '');
    setConfirmDevice(device);
  }, []);

  const handleConfirmAssign = useCallback(() => {
    if (!confirmDevice) return;
    onAssign(confirmDevice, assignLocation.trim());
    setConfirmDevice(null);
    setAssignLocation('');
  }, [confirmDevice, onAssign, assignLocation]);

  const handleCancelConfirm = useCallback(() => {
    setConfirmDevice(null);
    setAssignLocation('');
  }, []);

  return (
    <div className={`dp-drawer${open ? ' is-open' : ''}`}>
      <div className="dp-drawer__header">
        <h2 className="dp-drawer__title">Assign Device</h2>
        <button className="dp-drawer__close" onClick={onClose}>✕</button>
      </div>

      {/* Sticky area: search + reassign confirmation */}
      <div className="dp-drawer-sticky">
        <div className="dp-drawer__field">
          <label className="dp-drawer__label">Search Devices</label>
          <input
            type="text"
            className="dp-drawer__input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter by name, serial, or description…"
            autoFocus
          />
        </div>

        {/* Assign / reassign confirmation. Reassigning keeps the warning
            treatment because it pulls a live device out of another area; a fresh
            assign is routine, so it stays neutral. Mirrors AreaDeviceDrawer. */}
        {confirmDevice ? (
          <div
            className={`ap-reassign-confirm ${isReassign ? '' : 'ap-reassign-confirm--neutral'}`}
            role={isReassign ? 'alert' : 'group'}
            aria-label={isReassign ? undefined : 'Confirm assignment'}
          >
            <div className="ap-reassign-confirm__message">
              {isReassign ? (
                <svg className="ap-reassign-confirm__icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                </svg>
              ) : (
                <svg className="ap-reassign-confirm__icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
                </svg>
              )}
              <div>
                <p className="ap-reassign-confirm__title">
                  {isReassign ? 'Reassign device?' : 'Assign device?'}
                </p>
                <p className="ap-reassign-confirm__detail">
                  {isReassign ? (
                    <>
                      <strong>{confirmDevice.device_name}</strong> is currently assigned to{' '}
                      <strong>{confirmDevice.area_name}</strong>. Reassign to{' '}
                      <strong>{areaName}</strong>?
                    </>
                  ) : (
                    <>
                      <strong>{confirmDevice.device_name}</strong> will be assigned to{' '}
                      <strong>{areaName}</strong>.
                    </>
                  )}
                </p>
              </div>
            </div>

            {/* ap-field__* rather than the page's dp-drawer__* on purpose:
                dp-drawer__label has no `display: block`, so the label and input
                rendered on one line here while the AreasPage version stacked
                them. Same classes as that drawer = same layout by construction. */}
            <div className="ap-confirm-location">
              <label htmlFor="app-assign-location" className="ap-field__label">
                Location within area
              </label>
              <input
                id="app-assign-location"
                type="text"
                value={assignLocation}
                onChange={(e) => setAssignLocation(e.target.value)}
                placeholder="Optional... Bedroom, Bathroom, etc."
                className="ap-field__input"
                disabled={saving}
                autoFocus
              />
              <p className="ap-confirm-location__hint">
                {confirmDevice.descriptions
                  ? 'This is what will be saved. Edit it to change the location, or clear it to remove it.'
                  : 'Optional. Add a location if it helps staff find the device.'}
              </p>
            </div>

            <div className="ap-reassign-confirm__actions">
              <button
                type="button"
                className="dp-btn dp-btn--cancel dp-btn--sm"
                onClick={handleCancelConfirm}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`dp-btn dp-btn--sm ${isReassign ? 'dp-btn--warning' : 'dp-btn--primary'}`}
                onClick={handleConfirmAssign}
                disabled={saving}
              >
                {saving
                  ? (isReassign ? 'Reassigning…' : 'Assigning…')
                  : (isReassign ? 'Confirm and Reassign' : 'Confirm and Assign')}
              </button>
            </div>
          </div>
        ) : null}

        <label className="dp-drawer__label">
          Available Devices ({filteredDevices.length})
        </label>
      </div>

      {/* Scrollable device list */}
      <div className="dp-drawer__body">
        {/* Persistent so a brand-new device can be added without having to
            search and fail first. */}
        {onAddNewDevice ? (
          <button
            type="button"
            onClick={onAddNewDevice}
            className="dp-btn dp-btn--primary"
            style={{ width: '100%', marginBottom: 'var(--space-3)' }}
          >
            <i className="fe fe-plus" aria-hidden="true" style={{ marginRight: 6 }} />
            Add New Device
          </button>
        ) : null}

        <div className="ap-device-picker">
          {filteredDevices.length === 0 ? (
            <div className="ap-device-picker__empty">
              <p style={{ margin: 0 }}>
                {search.trim()
                  ? "No devices match your search. If it isn't in the system yet, add it here."
                  : 'No unassigned area devices available.'}
              </p>
              {/* Points back at the quick-add flow rather than off to the Devices
                  page — leaving the area was the confusion that flow removes.
                  The role-gated fallback to advanced setup lives inside it. */}
              {onAddNewDevice ? null : (
                <p style={{ margin: 'var(--space-2) 0 0' }}>
                  Can&apos;t find the device you&apos;re looking for?{' '}
                  <Link to="/community/devices" state={{ openAddDevice: true, addDeviceBase: 'area' }} style={{ color: 'var(--color-primary)', fontWeight: 'var(--font-weight-semibold)' }}>
                    Add a device
                  </Link>
                </p>
              )}
            </div>
          ) : null}
          {filteredDevices.map((device) => {
            const isAssignedToOther = Boolean(device.area_id && Number(device.is_active));
            return (
              <div
                key={device.device_id}
                className={`ap-device-picker__item${isAssignedToOther ? ' ap-device-picker__item--assigned' : ''}`}
              >
                <div className="ap-device-picker__info">
                  <div className="ap-device-picker__name">
                    <DeviceNameLink device={device} />
                  </div>
                  <div className="ap-device-picker__meta">
                    {device.inovonics_model_name || device.model_name ? <span>{device.inovonics_model_name || device.model_name}</span> : null}
                    {(device.inovonics_model_name || device.model_name) && device.manufacture_uuid ? <span> · </span> : null}
                    {device.manufacture_uuid ? <span>{device.manufacture_uuid}</span> : <span>—</span>}
                  </div>
                  {device.descriptions ? (
                    <div className="ap-device-picker__meta">{device.descriptions}</div>
                  ) : null}
                  {isAssignedToOther ? (
                    <div className="ap-device-picker__assigned-to">
                      <svg width="12" height="12" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
                      </svg>
                      Assigned to {device.area_name}
                    </div>
                  ) : null}
                </div>
                <button
                  type="button"
                  className={`ap-device-picker__btn ${isAssignedToOther ? 'ap-device-picker__btn--reassign' : 'ap-device-picker__btn--assign'}`}
                  onClick={() => handleAssign(device)}
                  disabled={saving}
                  title={isAssignedToOther
                    ? `Reassign ${device.device_name} from ${device.area_name}`
                    : `Assign ${device.device_name}`
                  }
                >
                  {isAssignedToOther ? (
                    <>
                      <svg width="14" height="14" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
                      </svg>
                      Reassign
                    </>
                  ) : (
                    <>
                      <svg width="14" height="14" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                      </svg>
                      Assign
                    </>
                  )}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer */}
      <div className="dp-drawer__footer">
        <button type="button" className="dp-btn dp-btn--cancel" onClick={onClose} disabled={saving}>
          Close
        </button>
      </div>
    </div>
  );
};

export default DeviceAssignDrawer;
